import React from 'react'
import GeometryTab from './tabs/GeometryTab'
import MaterialsTab from './tabs/MaterialsTab'
import LoadsTab from './tabs/LoadsTab'
import ResultsTab from './tabs/ResultsTab'
import ResponseTab from './tabs/ResponseTab'
import ReportTab from './tabs/ReportTab'
import { useTranslation } from '@structura/i18n'
import { CrossSection, SMBeamModel, TabType, LimitStateSelection, UiNotice, SectionMaterialStrength } from './types'

import { LoadState } from './hooks/useLoadState'
import { ResultsViewState } from './hooks/useResultsViewState'
import { ResponseViewState } from './hooks/useResponseViewState'
import { UseMaterialStateResult } from './hooks/useMaterialState'

interface GeometryCallbacks
{
  addSpan: () => void
  removeSpan: (spanId: number) => void
  updateNodeSupport: (nodeId: number, supportType: SMBeamModel['nodes'][number]['supportType']) => void
  updateSpanLength: (spanId: number, length: number) => void
}

interface LoadCallbacks
{
  addLoadToSpan: () => void
  removeLoad: (loadId: string, spanId: number) => void
  editLoad: (loadId: string, spanId: number) => void
}

interface TabContentProps
{
  activeStep: TabType
  model: SMBeamModel
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>
  geometryCallbacks: GeometryCallbacks
  getFilteredSectionPresets: (material: string) => Record<string, CrossSection>
  getSupportIcon: (supportType: SMBeamModel['nodes'][number]['supportType']) => string
  getSupportColor: (supportType: SMBeamModel['nodes'][number]['supportType']) => string
  sectionMaterialStrength: SectionMaterialStrength | null | undefined
  supportNotice: UiNotice | null
  onDismissSupportNotice: () => void
  materialState: UseMaterialStateResult
  calculateSelfWeight: (crossSection: CrossSection) => number
  loadState: LoadState
  loadCallbacks: LoadCallbacks
  selectedLimitState: LimitStateSelection
  setSelectedLimitState: (value: LimitStateSelection) => void
  resultsView: ResultsViewState
  result: any
  analyzeAllLimitStates: () => Promise<void>
  analyzingAll: boolean
  analyzeTimoshenko: () => Promise<void>
  analyzingTimoshenko: boolean
  analysisTheory: 'EulerBernoulli' | 'Timoshenko'
  lastAnalysisSummary: string[] | null
  selectedCombinationId: string
  setSelectedCombinationId: (id: string) => void
  responseView: ResponseViewState
  getSessionHeaders: () => Record<string, string>
  setResult: React.Dispatch<React.SetStateAction<any>>
  calculateResponse: () => Promise<void>
  calculatingResponse: boolean
  calculateSecondOrderEffects: (options?: { combinationId?: string }) => Promise<void>
  calculatingSecondOrderEffects: boolean
  secondOrderEffectsResult: any
  calculateDynamics: () => Promise<void>
  calculatingDynamics: boolean
  dynamicsResult: any
  sectionCatalogLoading: boolean
  sectionCatalogError: string | null
}

const SMBeamDesignerTabContent: React.FC<TabContentProps> = ({
  activeStep,
  model,
  setModel,
  geometryCallbacks,
  getFilteredSectionPresets,
  getSupportIcon,
  getSupportColor,
  sectionMaterialStrength,
  supportNotice,
  onDismissSupportNotice,
  materialState,
  calculateSelfWeight,
  loadState,
  loadCallbacks,
  selectedLimitState,
  setSelectedLimitState,
  resultsView,
  result,
  analyzeAllLimitStates,
  analyzingAll,
  analyzeTimoshenko,
  analyzingTimoshenko,
  analysisTheory,
  lastAnalysisSummary,
  selectedCombinationId,
  setSelectedCombinationId,
  responseView,
  getSessionHeaders,
  setResult,
  calculateResponse,
  calculatingResponse,
  calculateSecondOrderEffects,
  calculatingSecondOrderEffects,
  secondOrderEffectsResult,
  calculateDynamics,
  calculatingDynamics,
  dynamicsResult,
  sectionCatalogLoading,
  sectionCatalogError
}) => {
  const { addSpan, removeSpan, updateNodeSupport, updateSpanLength } = geometryCallbacks
  const { t } = useTranslation()
  const { selectedSpan, setSelectedSpan, selectedLoadType, setSelectedLoadType, loadForm, setLoadForm, editingLoadId, setEditingLoadId } = loadState
  const { addLoadToSpan, removeLoad, editLoad } = loadCallbacks

  const {
    currentMaterial,
    timberKind,
    materialDetails,
    concreteRelativeHumidity,
    concreteNotionalSize,
    concreteLoadingAge,
    concreteCementType,
    reliabilityClass,
    setReliabilityClass,
    applyRc1Reduction,
    setApplyRc1Reduction,
    imposedLoadCategory,
    setImposedLoadCategory
  } = materialState

  const {
    selectedDiagram,
    setSelectedDiagram,
    showValues,
    setShowValues,
    showMaxMin,
    setShowMaxMin,
    valueDecimals,
    setValueDecimals,
    diagramScale,
    setDiagramScale,
    valueDensity,
    setValueDensity,
    valueFontSize,
    setValueFontSize
  } = resultsView

  const {
    selectedDiagram: responseSelectedDiagram,
    setSelectedDiagram: setResponseSelectedDiagram,
    showValues: responseShowValues,
    setShowValues: setResponseShowValues,
    showMaxMin: responseShowMaxMin,
    setShowMaxMin: setResponseShowMaxMin,
    valueDecimals: responseValueDecimals,
    setValueDecimals: setResponseValueDecimals,
    diagramScale: responseDiagramScale,
    setDiagramScale: setResponseDiagramScale,
    valueDensity: responseValueDensity,
    setValueDensity: setResponseValueDensity,
    valueFontSize: responseValueFontSize,
    setValueFontSize: setResponseValueFontSize
  } = responseView

  switch (activeStep)
  {
    case 'geometry':
      return (
        <GeometryTab
          model={model}
          setModel={setModel}
          currentMaterial={currentMaterial}
          timberKind={timberKind}
          addSpan={addSpan}
          removeSpan={removeSpan}
          updateNodeSupport={updateNodeSupport}
          updateSpanLength={updateSpanLength}
          getFilteredSectionPresets={() => getFilteredSectionPresets(currentMaterial)}
          getSupportIcon={getSupportIcon}
          getSupportColor={getSupportColor}
          sectionMaterialStrength={sectionMaterialStrength}
          supportNotice={supportNotice}
          onDismissSupportNotice={onDismissSupportNotice}
          sectionCatalogLoading={sectionCatalogLoading}
          sectionCatalogError={sectionCatalogError}
        />
      )
    case 'materials':
      return (
        <MaterialsTab
          model={model}
          setModel={setModel}
          materialState={materialState}
          sectionMaterialStrength={sectionMaterialStrength}
        />
      )
    case 'loads':
      return (
        <LoadsTab
          model={model}
          setModel={setModel}
          selectedSpan={selectedSpan}
          setSelectedSpan={setSelectedSpan}
          selectedLoadType={selectedLoadType}
          setSelectedLoadType={setSelectedLoadType}
          loadForm={loadForm}
          setLoadForm={setLoadForm}
          addLoadToSpan={addLoadToSpan}
          removeLoad={removeLoad}
          editLoad={editLoad}
          editingLoadId={editingLoadId}
          setEditingLoadId={setEditingLoadId}
          calculateSelfWeight={calculateSelfWeight}
          currentMaterial={currentMaterial}
          timberKind={timberKind}
          sectionMaterialStrength={sectionMaterialStrength}
          materialDetails={materialDetails}
          concreteRelativeHumidity={concreteRelativeHumidity}
          concreteNotionalSize={concreteNotionalSize}
          concreteLoadingAge={concreteLoadingAge}
          concreteCementType={concreteCementType}
          reliabilityClass={reliabilityClass}
          setReliabilityClass={setReliabilityClass}
          applyRc1Reduction={applyRc1Reduction}
          setApplyRc1Reduction={setApplyRc1Reduction}
          imposedLoadCategory={imposedLoadCategory}
          setImposedLoadCategory={setImposedLoadCategory}
        />
      )
    case 'results':
      return (
        <ResultsTab
          model={model}
          result={result}
          selectedDiagram={selectedDiagram}
          setSelectedDiagram={setSelectedDiagram}
          showValues={showValues}
          setShowValues={setShowValues}
          showMaxMin={showMaxMin}
          setShowMaxMin={setShowMaxMin}
          valueDecimals={valueDecimals}
          setValueDecimals={setValueDecimals}
          diagramScale={diagramScale}
          setDiagramScale={setDiagramScale}
          valueDensity={valueDensity}
          setValueDensity={setValueDensity}
          valueFontSize={valueFontSize}
          setValueFontSize={setValueFontSize}
          currentMaterial={currentMaterial}
          timberKind={timberKind}
          onAnalyzeAll={analyzeAllLimitStates}
          analyzingAll={analyzingAll}
          onAnalyzeTimoshenko={analyzeTimoshenko}
          analyzingTimoshenko={analyzingTimoshenko}
          analysisTheory={analysisTheory}
          sectionMaterialStrength={sectionMaterialStrength}
          selectedLimitState={selectedLimitState}
          setSelectedLimitState={setSelectedLimitState}
          selectedCombinationId={selectedCombinationId}
          setSelectedCombinationId={setSelectedCombinationId}
          lastAnalysisSummary={lastAnalysisSummary}
          calculateSecondOrderEffects={calculateSecondOrderEffects}
          calculatingSecondOrderEffects={calculatingSecondOrderEffects}
          secondOrderEffectsResult={secondOrderEffectsResult}
          calculateDynamics={calculateDynamics}
          calculatingDynamics={calculatingDynamics}
          dynamicsResult={dynamicsResult}
        />
      )
    case 'response':
      return (
        <ResponseTab
          model={model}
          result={result}
          selectedDiagram={responseSelectedDiagram}
          setSelectedDiagram={setResponseSelectedDiagram}
          showValues={responseShowValues}
          setShowValues={setResponseShowValues}
          showMaxMin={responseShowMaxMin}
          setShowMaxMin={setResponseShowMaxMin}
          valueDecimals={responseValueDecimals}
          setValueDecimals={setResponseValueDecimals}
          diagramScale={responseDiagramScale}
          setDiagramScale={setResponseDiagramScale}
          valueDensity={responseValueDensity}
          setValueDensity={setResponseValueDensity}
          valueFontSize={responseValueFontSize}
          setValueFontSize={setResponseValueFontSize}
          getSessionHeaders={getSessionHeaders}
          setResult={setResult}
          onCalculateResponse={calculateResponse}
          calculatingResponse={calculatingResponse}
          currentMaterial={currentMaterial}
        />
      )
    case 'report':
      return (
        <ReportTab
          model={model}
          setModel={setModel}
          result={result}
          currentMaterial={currentMaterial}
          sectionMaterialStrength={sectionMaterialStrength || undefined}
          materialState={materialState}
          getSessionHeaders={getSessionHeaders}
        />
      )
    default:
      return <div>{t('beam.common.unknownTab')}</div>
  }
}

export default SMBeamDesignerTabContent

